/**
 * 원산지 주장 검사 — 1688(중국 공급처) 상품의 상품명·검색태그에 «국산·국내산·한국산·메이드인코리아»가 들어가면 뺀다(PR261017-2A7068).
 * 쿠팡 연관 키워드(«국산광목주머니»)나 1688 문구(国产 = 중국 기준 '자국산' = 중국산)를 AI 가 옮겨 와 원산지 오표시가 된다.
 * 근거 = 1688 속성의 원산지(产地) 값이 한국이거나, 상품 특징에 사용자가 한국산이라고 적은 경우뿐. 대상 키워드는 근거가 아니다.
 * 의존성 없는 순수 파일 — 단위시험 __tests__/seoOriginGuard.test.mjs
 */

// «외국산·중국산·미국산» 처럼 나라 이름 끝 글자가 앞에 붙은 «국산»만 원산지 한국 주장이 아니다.
// 다른 낱말 뒤에 붙은 «국산»(«남성벨트국산»)은 한국 주장이라 뺀다(«한국산»은 따로 본다)
// «자국산·본국산»은 国产 을 그대로 옮긴 말이라 낱말째 뺀다. «국산품·국산형» 같은 붙은 말도 낱말째(«품 양말» 남기지 않게)
const ORIGIN_CLAIM_RE = /(?:(?:자|본)국산|(?<![외중미영태타전제])국산|국내산|한국산)(?:제품|품|형)?|국내\s*(?:생산|제작|제조|공장)|메이드\s*인\s*코리아|made\s*in\s*korea|韩国制造|韩国产/gi;
const ORIGIN_ATTR = /원산지|산지|생산지|제조국|产地|原产地|产区|origin/i;
// 1688 원산지 값의 «국산·국내산»은 国产(중국 기준 자국산 = 중국산)을 옮긴 말이라 한국 근거가 아니다 — 나라 이름만 본다
const KOREA_RE = /한국|대한민국|韩国|korea/i;

function tidy(s) {
  return String(s).replace(/\s{2,}/g, ' ').replace(/^\s+|\s+$/g, '');
}

// 상품 특징의 금지·부정 문장(«국산 표기 금지»·«국산이라고 쓰지 말 것»·«국산 아님»)은 한국산 근거가 아니다(D176A0 후속 · cpl-505a-seomaterial-2)
const FEATURE_NEG_RE = /금지|말\s*것|하지\s*(?:마|말|않)|쓰지|넣지|표기\s*(?:불가|X)|아님|아닙|아닌/;

/**
 * 한국산이라는 근거가 있나 — 1688 원산지 속성 값이 한국 / 상품 특징(금지·부정 문장 뺀 글)에 한국산이라고 적음.
 * 1688 원산지 속성 값이 중국이면 상품 특징과 무관하게 근거 없음.
 * @param {{ attributes?: Array, productFeature?: string }} src
 */
export function hasKoreanOriginBasis(src) {
  const s = src || {};
  const attrs = Array.isArray(s.attributes) ? s.attributes.filter(Boolean) : [];
  const originVals = attrs.filter(a => ORIGIN_ATTR.test(`${a.attributeName || ''} ${a.attributeNameTrans || ''}`))
    .map(a => `${a.value || ''} ${a.valueTrans || ''}`);
  if (originVals.some(v => KOREA_RE.test(v))) return true;
  if (originVals.some(v => /中国|중국|china/i.test(v))) return false;
  const feature = String(s.productFeature || '').split(/[.。\n!;]+/).filter(x => !FEATURE_NEG_RE.test(x)).join('. ');
  return new RegExp(ORIGIN_CLAIM_RE.source, 'i').test(feature);
}

/**
 * 중국 공급처 상품인가 — 1688 원 제목에 한자가 있거나 구매링크가 1688. 둘 다 모르면(값 없음) 중국으로 본다(로켓 등록은 1688 상품).
 * 한국 도매처 상품(원 제목이 한글, 링크가 1688 아님)은 건드리지 않는다.
 * @param {{ originalTitle?: string, purchaseLink?: any }} src
 */
export function isChinaSourced(src) {
  const s = src || {};
  const title = String(s.originalTitle || '');
  const link = s.purchaseLink == null ? '' : JSON.stringify(s.purchaseLink);
  if (/[一-鿿]/.test(title) || /1688\.com|alibaba|taobao|tmall/i.test(link)) return true;
  return !title && !link.replace(/^"|"$/g, '');
}

function skip(src) {
  return !isChinaSourced(src) || hasKoreanOriginBasis(src);
}

/**
 * 상품명에서 원산지 한국 주장을 뺀다.
 * @param {string} productName
 * @param {{ attributes?: Array, productFeature?: string, originalTitle?: string, purchaseLink?: any }} src
 * @param {{ allowEmpty?: boolean }} [opts] - allowEmpty: 다 빠지면 빈 이름(되살림 0 · 부른 쪽이 크게 실패)
 * @returns {{ name: string, removed: string[] }}
 */
export function dropOriginClaims(productName, src, opts) {
  if (!productName || typeof productName !== 'string') return { name: productName, removed: [] };
  if (skip(src)) return { name: productName, removed: [] };
  const removed = [];
  // 빼낸 자리 옆의 «/» 도 같이 뺀다(«국산/북미산 호두나무» → «북미산 호두나무», 실측 251209011001)
  const name = productName.replace(ORIGIN_CLAIM_RE, (m) => { removed.push(m); return '\uE000'; }).replace(/\/?\uE000\/?/g, ' ');
  if (!removed.length) return { name: productName, removed };
  const cleaned = tidy(name);
  if (!cleaned && !(opts && opts.allowEmpty)) return { name: productName, removed: [] };
  return { name: cleaned, removed };
}

/**
 * 검색태그에서 원산지 한국 주장을 뺀다 — 낱말만 빼고 남은 말이 있으면 태그로 남긴다(«국산광목주머니» → «광목주머니»). 겹치면 하나만.
 * @param {string[]} keywords
 * @param {{ attributes?: Array, productFeature?: string }} src
 * @returns {{ keywords: string[], removed: string[] }}
 */
export function dropOriginClaimTags(keywords, src) {
  if (!Array.isArray(keywords)) return { keywords, removed: [] };
  if (skip(src)) return { keywords, removed: [] };
  const removed = [];
  const out = [];
  const seen = new Set();
  keywords.forEach(k => {
    const raw = String(k == null ? '' : k);
    let hit = false;
    const left = tidy(raw.replace(ORIGIN_CLAIM_RE, () => { hit = true; return ' '; }));
    if (hit) removed.push(raw);
    const v = hit ? left : raw;
    const key = v.replace(/\s+/g, '');
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(v);
  });
  if (!removed.length) return { keywords, removed };
  return { keywords: out, removed };
}

/**
 * 근거 없는 아동 대상 — 성인·일반용 상품명에 쿠팡 연관 키워드(«여아 머리핀 정리대»)의 «여아·남아·유아…»를 AI 가 옮긴다(PR261017-3BB158).
 * 카테고리 이름을 모르면(« (64554)» 처럼 번호만) stripKidsWords 가 손대지 않아 따로 본다.
 * 중국 공급처 상품만 본다(isChinaSourced). 근거 = 원 제목·원 태그(originalTags)·속성·남긴 옵션·상품 특징·대상 키워드에 아동(儿童·童·宝宝·婴·女孩·男孩·kids·아동…)이 있으면 남긴다.
 * «소녀풍»(少女风)은 꾸밈새지 대상이 아니다.
 */
// 낱말 경계(앞뒤가 끝·띄어쓰기·문장부호)일 때만 뺀다 — 붙은 말(«베이비핑크»·«키즈카페»)은 다른 낱말이라 남긴다.
// 붙은 말이라도 뒷말(1번 묶음)이 대상 키워드의 품목이면(«유아머리핀» + 대상 키워드 «머리핀정리대») 대상 수식어라 뺀다.
const KIDS_NAME_RE = /(?<![가-힣A-Za-z0-9])(?:여아|남아|유아|아동|어린이|키즈|아기|베이비|초등)(?:용|전용)?(?!자기)(?=$|[\s,/·)\]]|([가-힣A-Za-z0-9]{2,}))/g;
const KIDS_SRC_RE = /儿童|童|宝宝|婴|幼|女孩|男孩|小孩|kids?|child|baby|toddler|여아|남아|유아|아동|어린이|키즈|아기|베이비|초등/i;

/**
 * @param {string} productName
 * @param {{ originalTitle?: string, originalTags?: string[], purchaseLink?: any, attributes?: Array, options?: Array, productFeature?: string, targetKeyword?: string }} src
 * @param {{ allowEmpty?: boolean }} [opts] - allowEmpty: 다 빠지면 빈 이름(되살림 0 · 부른 쪽이 크게 실패)
 * @returns {{ name: string, removed: string[] }}
 */
export function dropKidsWordsNotInSource(productName, src, opts) {
  if (!productName || typeof productName !== 'string') return { name: productName, removed: [] };
  const s = src || {};
  // 한국 도매처 상품(원 제목 한글·링크 1688 아님)은 건드리지 않는다 — «데이나 여아 원피스 국산»의 «여아»는 원 제목이 근거
  if (!isChinaSourced(s)) return { name: productName, removed: [] };
  const title = String(s.originalTitle || '');
  const basis = [
    title,
    ...(Array.isArray(s.originalTags) ? s.originalTags : []).map(t => String(t == null ? '' : t)),
    ...(Array.isArray(s.attributes) ? s.attributes : []).filter(Boolean).map(a => `${a.value || ''} ${a.valueTrans || ''}`),
    ...(Array.isArray(s.options) ? s.options : []).filter(Boolean)
      .map(o => ['option1', 'option2'].map(k => (o[k] ? `${o[k].name || ''} ${o[k].nameTrans || ''}` : '')).join(' ')),
    String(s.productFeature || ''),
    String(s.targetKeyword || '')
  ].join(' \n ');
  if (KIDS_SRC_RE.test(basis)) return { name: productName, removed: [] };
  const removed = [];
  const item = String(s.targetKeyword || '').replace(/\s+/g, '');
  const name = productName.replace(KIDS_NAME_RE, (m, glued) => {
    if (glued && !(item && (item.includes(glued) || glued.includes(item)))) return m;
    removed.push(m);
    return glued ? '' : ' ';
  });
  if (!removed.length) return { name: productName, removed };
  const cleaned = tidy(name);
  if (!cleaned && !(opts && opts.allowEmpty)) return { name: productName, removed: [] };
  return { name: cleaned, removed };
}

export default { isChinaSourced, hasKoreanOriginBasis, dropOriginClaims, dropOriginClaimTags, dropKidsWordsNotInSource };
